/**
 * Resolve authored route geometry (normal + alt −1 / −2) for map display.
 * Dev localStorage overrides are applied when provided.
 */
import { EXPLORE_UNKNOWN_PORTAL_LAYOUT } from '../exploreUnknownPortalLayout.js';
import {
  buildCanonicalSurfaceMissionIndices,
  getAlternativePathDisplayLayout,
  getNormalPathDisplayLayout,
} from './exploreUnknownAlternativePathLayout.js';
import { applyRunPlanToAltLayout, isAlternativePathEligibleSurface } from './exploreUnknownAlternativePath.js';
import { loadDevWaypoints } from './exploreUnknownWaypointDevStorage.js';

const SURFACE_LAYOUT_PREFIX = {
  arrival: 'arrival',
  cave: 'cave',
  desert: 'desert',
  fair: 'fair',
  lavender: 'lavender',
  boat: 'boat',
  pixelpast: 'pixelPast',
  kendrex: 'kendrex',
  autobahn: 'autobahn',
  productionhell: 'productionHell',
  awakening: 'awakening',
};

/** @typedef {'normal'|'altMinus1'|'altMinus2'} ExploreUnknownRoutePathMode */

/**
 * @param {string} surface
 * @param {ExploreUnknownRoutePathMode} pathMode
 * @param {number} mapMissionOffset
 * @param {Record<string, object>} alternativePathPlan
 * @param {number[][] | null} [devOverride]
 */
export function resolveRoutePointsForSurface(
  surface,
  pathMode,
  mapMissionOffset,
  alternativePathPlan,
  devOverride = null
) {
  if (devOverride != null) {
    return devOverride.map((p) => [...p]);
  }
  if (pathMode !== 'normal' && !isAlternativePathEligibleSurface(surface)) {
    return resolveRoutePointsForSurface(
      surface,
      'normal',
      mapMissionOffset,
      alternativePathPlan,
      devOverride
    );
  }
  if (process.env.NODE_ENV === 'development') {
    try {
      const stored = loadDevWaypoints(surface, pathMode);
      if (stored != null) {
        return stored.map((p) => [...p]);
      }
    } catch {
      /* fall through to bundled layout */
    }
  }
  const prefix = SURFACE_LAYOUT_PREFIX[surface];
  if (pathMode === 'normal') {
    if (surface === 'awakening') return [...(EXPLORE_UNKNOWN_PORTAL_LAYOUT.awakeningPointsOfInterest ?? [])];
    if (surface === 'productionhell') return [...(EXPLORE_UNKNOWN_PORTAL_LAYOUT.productionHellPointsOfInterest ?? [])];
    if (surface === 'autobahn') return [...(EXPLORE_UNKNOWN_PORTAL_LAYOUT.autobahnPointsOfInterest ?? [])];
    if (surface === 'kendrex') return [...(EXPLORE_UNKNOWN_PORTAL_LAYOUT.kendrexPointsOfInterest ?? [])];
    if (surface === 'pixelpast') return [...(EXPLORE_UNKNOWN_PORTAL_LAYOUT.pixelPastPointsOfInterest ?? [])];
    if (surface === 'boat') return [...(EXPLORE_UNKNOWN_PORTAL_LAYOUT.boatPointsOfInterest ?? [])];
    if (surface === 'treetop') return [...(EXPLORE_UNKNOWN_PORTAL_LAYOUT.treetopPointsOfInterest ?? [])];
    if (surface === 'lavender') return [...(EXPLORE_UNKNOWN_PORTAL_LAYOUT.lavenderPointsOfInterest ?? [])];
    if (surface === 'fair') return [...(EXPLORE_UNKNOWN_PORTAL_LAYOUT.fairPointsOfInterest ?? [])];
    if (surface === 'desert') return [...EXPLORE_UNKNOWN_PORTAL_LAYOUT.desertPointsOfInterest];
    if (surface === 'cave') return [...EXPLORE_UNKNOWN_PORTAL_LAYOUT.cavePointsOfInterest];
    return [...EXPLORE_UNKNOWN_PORTAL_LAYOUT.arrivalPointsOfInterest];
  }
  if (prefix) {
    const altKey =
      pathMode === 'altMinus2'
        ? `${prefix}AlternativeMinus2PointsOfInterest`
        : `${prefix}AlternativeMinus1PointsOfInterest`;
    const authored = EXPLORE_UNKNOWN_PORTAL_LAYOUT[altKey];
    if (Array.isArray(authored) && authored.length > 0) {
      return authored.map((p) => [...p]);
    }
  }
  const canonical = buildCanonicalSurfaceMissionIndices(surface, mapMissionOffset);
  const missionsRemoved = pathMode === 'altMinus2' ? 2 : 1;
  const layout = getAlternativePathDisplayLayout(
    surface,
    missionsRemoved,
    mapMissionOffset + 1,
    canonical
  );
  return layout.points.map((p) => [...p]);
}

/**
 * Keep POI coords and per-point mission indices aligned (authored POI counts can drift in dev).
 * @param {{ missionIndices: number[], forkPoint?: number[], points?: number[][] }} layout
 * @param {number[][]} points
 */
function attachPointsToRouteLayout(layout, points) {
  if (!layout || !Array.isArray(points)) {
    throw new Error('attachPointsToRouteLayout: layout and points are required');
  }
  const missionIndices = Array.isArray(layout.missionIndices)
    ? layout.missionIndices.map((m) => Number(m))
    : [];
  if (missionIndices.length === points.length) {
    return { ...layout, points, missionIndices };
  }
  if (missionIndices.length > points.length) {
    return { ...layout, points, missionIndices: missionIndices.slice(0, points.length) };
  }
  return { ...layout, points: points.slice(0, missionIndices.length), missionIndices };
}

/**
 * Surfaces without alt-path forks (e.g. treetop) only expose the normal route geometry.
 * @param {string} surface
 * @param {number} mapMissionOffset
 * @param {{ normal?: number[][] | null }} [devOverrides]
 */
function buildNormalOnlyRouteBundle(surface, mapMissionOffset, devOverrides = {}) {
  const normalPoints = resolveRoutePointsForSurface(
    surface,
    'normal',
    mapMissionOffset,
    {},
    devOverrides.normal ?? null
  );
  const missionIndices = Array.from(
    { length: normalPoints.length },
    (_, i) => mapMissionOffset + 1 + i
  );
  const forkPoint =
    normalPoints.length >= 2
      ? [
          Math.round((normalPoints[0][0] + normalPoints[1][0]) / 2),
          Math.round((normalPoints[0][1] + normalPoints[1][1]) / 2),
        ]
      : normalPoints[0]
        ? [...normalPoints[0]]
        : [0, 0];
  const normal = attachPointsToRouteLayout(
    { forkPoint, points: normalPoints, missionIndices },
    normalPoints
  );
  return {
    normal,
    alt1: normal,
    alt2: normal,
    runAlt: normal,
    runMissionsRemoved: 0,
  };
}

/**
 * @param {string} surface
 * @param {number} mapMissionOffset
 * @param {Record<string, object>} alternativePathPlan
 * @param {{ normal?: number[][] | null, altMinus1?: number[][] | null, altMinus2?: number[][] | null }} [devOverrides]
 */
export function buildSurfaceRouteBundle(surface, mapMissionOffset, alternativePathPlan, devOverrides = {}) {
  if (!isAlternativePathEligibleSurface(surface)) {
    return buildNormalOnlyRouteBundle(surface, mapMissionOffset, devOverrides);
  }
  const firstMissionIndex = mapMissionOffset + 1;
  const canonical = buildCanonicalSurfaceMissionIndices(surface, mapMissionOffset);
  const buildAltLayout = (surfaceKey, removed, first, normals) =>
    getAlternativePathDisplayLayout(surfaceKey, removed, first, normals);

  const normalPoints = resolveRoutePointsForSurface(
    surface,
    'normal',
    mapMissionOffset,
    alternativePathPlan,
    devOverrides.normal ?? null
  );
  const alt1Points = resolveRoutePointsForSurface(
    surface,
    'altMinus1',
    mapMissionOffset,
    alternativePathPlan,
    devOverrides.altMinus1 ?? null
  );
  const alt2Points = resolveRoutePointsForSurface(
    surface,
    'altMinus2',
    mapMissionOffset,
    alternativePathPlan,
    devOverrides.altMinus2 ?? null
  );

  const normalLayout = getNormalPathDisplayLayout(surface, canonical);
  const alt1Layout = buildAltLayout(surface, 1, firstMissionIndex, canonical);
  const alt2Layout = buildAltLayout(surface, 2, firstMissionIndex, canonical);

  const runMissionsRemoved = alternativePathPlan?.[surface]?.missionsRemoved === 2 ? 2 : 1;
  const runAltBase = runMissionsRemoved === 2 ? alt2Layout : alt1Layout;
  const runAltLayout = applyRunPlanToAltLayout(
    runAltBase,
    surface,
    alternativePathPlan,
    runMissionsRemoved,
    canonical
  );
  const runAltPoints = runMissionsRemoved === 2 ? alt2Points : alt1Points;

  return {
    normal: attachPointsToRouteLayout(normalLayout, normalPoints),
    alt1: attachPointsToRouteLayout(alt1Layout, alt1Points),
    alt2: attachPointsToRouteLayout(alt2Layout, alt2Points),
    runAlt: attachPointsToRouteLayout(runAltLayout, runAltPoints),
    runMissionsRemoved,
  };
}

/**
 * Rite of respite marker beside the milestone mission on a route layout.
 * @param {string} surface
 * @param {number|null} milestoneMission
 * @param {number[]} missionIndicesPerPoint
 * @param {number[][]} points
 * @returns {number[]|null}
 */
export function resolveDarknessBargainAuthoringPointForRoute(
  surface,
  milestoneMission,
  missionIndicesPerPoint,
  points
) {
  if (milestoneMission == null) return null;
  if (!Array.isArray(missionIndicesPerPoint) || !Array.isArray(points)) return null;
  const localIndex = missionIndicesPerPoint.indexOf(Number(milestoneMission));
  if (localIndex < 0 || localIndex >= points.length) return null;
  const off = EXPLORE_UNKNOWN_PORTAL_LAYOUT.darknessBargainOffsetBySurface?.[surface];
  if (!Array.isArray(off) || off.length !== 2) return null;
  const anchor = points[localIndex];
  if (!Array.isArray(anchor) || anchor.length !== 2) return null;
  const [mx, my] = anchor;
  return [mx + off[0], my + off[1]];
}
